import toast from "react-hot-toast";
import axiosInstance from "helpers/api";
import { formatErrorForReact } from "helpers/utils";
import Logo from "components/common/Logo";
import { useCookies } from "react-cookie";
import Button from "components/base/Button";
import { loadStripe } from "@stripe/stripe-js";
import { Fragment, useEffect, useState } from "react";
import configService from "services/configService";
import { useForm, SubmitHandler } from "react-hook-form";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";

type Inputs = {
  phone: string;
  email: string;
  password: string;
  last_name: string;
  first_name: string;
  confirm_password: string;
};

const SignUpPaidForm = () => {
  const [cookie, setCookie] = useCookies(["isLoggedInKIQ", "stripeIdpKey"]);
  
  // Initialize Stripe with secure configuration
  const [stripePromise, setStripePromise] = useState<Promise<any> | null>(null);

  useEffect(() => {
    const initializeStripe = async () => {
      try {
        const stripeConfig = await configService.getStripeConfig();
        const stripe = loadStripe(stripeConfig.publishableKey);
        setStripePromise(stripe);
        console.log('✅ [SignUp] Initialized Stripe with secure publishable key');
      } catch (error) {
        console.warn('⚠️ [SignUp] Failed to get secure Stripe config, using fallback');
        const fallbackKey = process.env.REACT_APP_STRIPE_PUB_KEY as string;
        if (fallbackKey) {
          const stripe = loadStripe(fallbackKey);
          setStripePromise(stripe);
        } else {
          console.error('❌ [SignUp] No Stripe publishable key available');
          toast.error('Payment system unavailable. Please contact support.');
        }
      }
    };

    initializeStripe();
  }, []);  

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<Inputs>();

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  const interval = searchParams.get("interval");
  const error = searchParams.get("error");
  const passwordValue = watch("password");

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    setIsLoading(true);
    try {
      let idempotency_key = cookie?.stripeIdpKey;
    
      if (!idempotency_key) {
        idempotency_key = crypto?.randomUUID();
        setCookie('stripeIdpKey', idempotency_key, {
          path: '/',
          maxAge: 86400 // 24 hours
        });
      }

      const response = await axiosInstance.post("/auth/paid-registration", {
        plan_name: type,
        idempotency_key,
        email: data.email,
        phone: data.phone,
        interval: interval,
        password: data.password,
        first_name: data.first_name,
        last_name: data.last_name,
        confirm_password: data.confirm_password,
      });

      if (!stripePromise) {
        throw new Error('Stripe is not initialized. Please refresh the page and try again.');
      }

      const stripe = await stripePromise;
      
      if (!stripe) {
        throw new Error('Stripe failed to initialize. Check your connection and try again.');
      }

      const result = await stripe.redirectToCheckout({
        sessionId: response.data.id,
      });
      if (result?.error) {
        console.log(result.error);
        throw new Error(result.error.message || 'Payment processing failed');
      }
    } catch (error: unknown) {
      let errorMessage = "Registration failed";
      if (error && typeof error === 'object' && 'response' in error && error.response && typeof error.response === 'object' && 'data' in error.response && error.response.data && typeof error.response.data === 'object' && 'message' in error.response.data) {
        errorMessage = String(error.response.data.message);
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(formatErrorForReact(errorMessage));
    }
    setIsLoading(false);
  };

  const handleGoogleSignUp = async () => {
    setIsGoogleLoading(true);
    try {
      // Use the dedicated OAuth signup endpoint for paid plans
      if(process.env.NODE_ENV === "development") {
        window.location.href = `http://localhost:8080/api/google/signup/auth?signup=true&paid=true&plan=${type}&interval=${interval}`;
      } else {
        window.location.href = `${process.env.APP_URL}/api/google/signup/auth?signup=true&paid=true&plan=${type}&interval=${interval}`;
      }
    } catch (error) {
      toast.error("Google sign-up failed");
      setIsGoogleLoading(false);
    }
  };

  const navigate = useNavigate();

  useEffect(() => {
    if (cookie.isLoggedInKIQ) {
      navigate("/dashboard");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookie.isLoggedInKIQ]);

  // Show toast for existing email error immediately on component mount
  useEffect(() => {
    // Check for error parameter on component mount
    if (error === "email_exists") {
      // Display the error toast
      toast.error("This email is already registered. Please sign in instead.", {
        duration: 5000, // Show for 5 seconds
        position: "top-center",
        style: {
          background: "#FEE2E2",
          color: "#B91C1C",
          fontWeight: 500
        },
      });
      
      // Clear the error parameter from URL after a short delay
      setTimeout(() => {
        const newParams = new URLSearchParams(searchParams.toString());
        newParams.delete("error");
        navigate(`/sign-up?${newParams.toString()}`, { replace: true });
      }, 300); // Small delay to ensure toast is shown
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Fragment>
      <div className="text-center mb-4">
        <Link to="/" className="d-flex flex-center text-decoration-none mb-3">
          <Logo
            text={false}
            width={100}
            className="fw-bolder fs-5 d-inline-block"
          />
        </Link>
        <div className="d-flex align-items-center justify-content-center mb-3">
          <div className="bg-primary rounded-circle p-3 me-3" style={{ background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)' }}>
            <i className="fas fa-crown text-white" style={{ fontSize: '24px' }}></i>
          </div>
          <div>
            <h3 className="text-body-highlight mb-1" style={{ color: '#1f2937', fontWeight: '700' }}>
              Subscribe to {type} Plan
            </h3>
            <p className="text-body-tertiary mb-0" style={{ fontSize: '14px', color: '#6b7280' }}>
              {interval === 'month' ? 'Monthly' : 'Annual'} subscription • Advanced features
            </p>
          </div>
        </div>
        <p className="text-body-tertiary" style={{ color: '#4b5563' }}>
          Complete your account setup and start your {type} plan today
        </p>
      </div>

      {/* <div className="mb-4">
        <Button
          variant="outline-primary"
          onClick={handleGoogleSignUp}
          disabled={isGoogleLoading}
          className="w-100 d-flex align-items-center justify-content-center py-3"
          style={{
            border: '2px solid #e5e7eb',
            background: 'white',
            color: '#374151',
            fontWeight: '600',
            fontSize: '16px',
            borderRadius: '12px',
            transition: 'all 0.3s ease'
          }}
        >
          {isGoogleLoading ? (
            <div className="spinner-border spinner-border-sm me-2" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          ) : (
            <svg className="me-2" width="20" height="20" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
          )}
          Continue with Google & Skip to Payment
        </Button>
      </div> */}

      {/* Divider */}
      {/* <div className="d-flex align-items-center mb-4">
        <hr className="flex-grow-1" style={{ borderColor: '#e5e7eb' }} />
        <span className="px-3 text-body-tertiary" style={{ fontSize: '14px', color: '#9ca3af' }}>
          Or fill out details manually
        </span>
        <hr className="flex-grow-1" style={{ borderColor: '#e5e7eb' }} />
      </div> */}

      <Form onSubmit={handleSubmit(onSubmit)}>
        <Row className="g-3 mb-3">
          <Col lg={6}>
            <Form.Group className="text-start">
              <Form.Label htmlFor="first_name" style={{ color: '#374151', fontWeight: '500' }}>
                First Name
                <span className="text-danger"> *</span>
              </Form.Label>
              <Form.Control
                id="first_name"
                type="text"
                placeholder="John"
                style={{
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  padding: '12px 16px',
                  fontSize: '16px'
                }}
                {...register("first_name", {
                  required: true,
                  maxLength: 200,
                })}
              />
              {errors.first_name?.type === "required" && (
                <p role="alert" className="text-danger" style={{ fontSize: '14px', marginTop: '4px' }}>
                  First name is required
                </p>
              )}
            </Form.Group>
          </Col>
          <Col lg={6}>
            <Form.Group className="text-start">
              <Form.Label htmlFor="last_name" style={{ color: '#374151', fontWeight: '500' }}>
                Last Name
              </Form.Label>
              <Form.Control
                id="last_name"
                type="text"
                placeholder="Doe"
                style={{
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  padding: '12px 16px',
                  fontSize: '16px'
                }}
                {...register("last_name", {
                  maxLength: 200,
                })}
              />
            </Form.Group>
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col lg={6}>
            <Form.Group className="text-start">
              <Form.Label htmlFor="phone" style={{ color: '#374151', fontWeight: '500' }}>
                Phone Number
                <span className="text-danger"> *</span>
              </Form.Label>
              <Form.Control
                id="phone"
                type="tel"
                inputMode="tel"
                placeholder="(555) 123-4567"
                style={{
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  padding: '12px 16px',
                  fontSize: '16px'
                }}
                {...register("phone", { required: true, maxLength: 100 })}
              />
              {errors.phone?.type === "required" && (
                <p role="alert" className="text-danger" style={{ fontSize: '14px', marginTop: '4px' }}>
                  Phone number is required
                </p>
              )}
            </Form.Group>
          </Col>
          <Col lg={6}>
            <Form.Group className="text-start">
              <Form.Label htmlFor="email" style={{ color: '#374151', fontWeight: '500' }}>
                Email Address
                <span className="text-danger"> *</span>
              </Form.Label>
              <Form.Control
                id="email"
                type="email"
                placeholder="john@company.com"
                style={{
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  padding: '12px 16px',
                  fontSize: '16px'
                }}
                {...register("email", { required: true, maxLength: 100 })}
              />
              {errors.email?.type === "required" && (
                <p role="alert" className="text-danger" style={{ fontSize: '14px', marginTop: '4px' }}>
                  Email address is required
                </p>
              )}
            </Form.Group>
          </Col>
        </Row>

        <Row className="g-3 mb-4">
          <Col lg={6}>
            <Form.Group className="position-relative text-start">
              <Form.Label htmlFor="password" style={{ color: '#374151', fontWeight: '500' }}>
                Password<span className="text-danger"> *</span>
              </Form.Label>
              <Form.Control
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                className="pe-5"
                style={{
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  padding: '12px 16px',
                  fontSize: '16px'
                }}
                autoComplete="new-password"
                {...register("password", {
                  required: true,
                  minLength: {
                    value: 8,
                    message: "Password must be at least 8 characters long.",
                  },
                  maxLength: {
                    value: 40,
                    message: "Password cannot exceed 40 characters.",
                  },
                })}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="btn btn-link p-0 position-absolute end-0 translate-middle-y me-2"
                style={{ textDecoration: "none", top: "72%" }}
              >
                <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
              </button>
              {errors.password?.type === "required" && (
                <p role="alert" className="text-danger" style={{ fontSize: '14px', marginTop: '4px' }}>
                  Password is required
                </p>
              )}
              {errors.password && errors.password.message && (
                <p role="alert" className="text-danger" style={{ fontSize: '14px', marginTop: '4px' }}>
                  {errors.password.message}
                </p>
              )}
            </Form.Group>
          </Col>
          <Col lg={6}>
            <Form.Group className="position-relative text-start">
              <Form.Label htmlFor="confirmPassword" style={{ color: '#374151', fontWeight: '500' }}>
                Confirm Password<span className="text-danger"> *</span>
              </Form.Label>
              <Form.Control
                id="confirmPassword"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="••••••••"
                className="pe-5"
                style={{
                  borderRadius: '8px',
                  border: '2px solid #e5e7eb',
                  padding: '12px 16px',
                  fontSize: '16px'
                }}
                autoComplete="new-password"
                {...register("confirm_password", {
                  required: "Confirm password is required",
                  validate: (value) =>
                    value === passwordValue || "Passwords do not match",
                })}
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword((prev) => !prev)}
                className="btn btn-link p-0 position-absolute end-0 translate-middle-y me-2"
                style={{ textDecoration: "none", top: "72%" }}
              >
                <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
              </button>
              {errors.confirm_password && (
                <p role="alert" className="text-danger" style={{ fontSize: '14px', marginTop: '4px' }}>
                  {errors.confirm_password.message}
                </p>
              )}
            </Form.Group>
          </Col>
        </Row>

        {/* Enhanced Terms Notice */}
        <div className="mb-4 p-3" style={{
          background: 'rgba(0, 212, 255, 0.05)',
          borderRadius: '8px',
          border: '1px solid rgba(0, 212, 255, 0.1)'
        }}>
          <p className="mb-0" style={{ fontSize: '14px', color: '#4b5563', lineHeight: '1.5' }}>
            By continuing, you agree to KairosIQ's{" "}
            <Link 
              to="/terms-and-conditions" 
              target="_blank"
              style={{ color: '#00d4ff', textDecoration: 'none' }}
            >
              Terms of Service
            </Link>
            {" "}and{" "}
            <Link 
              to="/privacy-policy" 
              target="_blank"
              style={{ color: '#00d4ff', textDecoration: 'none' }}
            >
              Privacy Policy
            </Link>
            , including the use of AI to analyze email communications for real estate insights.
          </p>
        </div>

        {/* Continue to Payment Button */}
        <Button
          disabled={isLoading}
          variant="primary"
          type="submit"
          className="w-100 mt-2"
          style={{
            background: 'linear-gradient(135deg, #00d4ff 0%, #0ea5e9 100%)',
            border: 'none',
            borderRadius: '8px',
            padding: '14px 24px',
            fontSize: '16px',
            fontWeight: '600',
            boxShadow: '0 4px 15px rgba(0, 212, 255, 0.3)'
          }}
        >
          {isLoading ? (
            <Spinner animation="border" role="status" size="sm" />
          ) : (
            <>
              <i className="fas fa-credit-card me-2"></i>
              Continue to Payment
            </>
          )}
        </Button>

        {/* Sign In Link */}
        <div className="text-center mt-3">
          <span style={{ color: '#6b7280', fontSize: '14px' }}>
            Already have an account?{" "}
            <Link 
              to="/sign-in" 
              style={{ 
                color: '#00d4ff', 
                textDecoration: 'none', 
                fontWeight: '600' 
              }}
            >
              Sign in here
            </Link>
          </span>
        </div>
      </Form>

      <div 
        className="mt-4 p-3 text-center"
        style={{
          background: 'rgba(0, 212, 255, 0.05)',
          borderRadius: '8px',
          border: '1px solid rgba(0, 212, 255, 0.1)'
        }}
      >
        <i className="fas fa-shield-alt text-primary me-2"></i>
        <span style={{ fontSize: '12px', color: '#4b5563' }}>
          Secure checkout powered by Stripe • Your data is encrypted and protected
        </span>
      </div>
    </Fragment>
  );
};

export default SignUpPaidForm;
